import { useState } from "react";
import {
  confirmPropertyCsvImport,
  getPropertyImportTemplateUrl,
  previewPropertyCsvImport,
} from "../../../services/midpointApi";
import { formatPropertyLabel, formatPropertyPrice } from "../../../utils/realEstateFormat";

const AdminPropertyImportPage = ({ onClose }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setPreview(null);
    setImportResult(null);
    setErrorMessage("");
  };

  const handlePreview = async () => {
    if (!selectedFile) {
      setErrorMessage("Choose a CSV file before previewing.");
      return;
    }

    try {
      setPreviewLoading(true);
      setErrorMessage("");
      setImportResult(null);
      const result = await previewPropertyCsvImport(selectedFile);
      setPreview(result);
    } catch (error) {
      setPreview(null);
      setErrorMessage(error.message || "Unable to preview CSV file.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedFile) {
      setErrorMessage("Choose a CSV file before importing.");
      return;
    }

    if (!preview?.summary?.validRows) {
      setErrorMessage("Preview the CSV and ensure at least one valid row before importing.");
      return;
    }

    try {
      setConfirmLoading(true);
      setErrorMessage("");
      const result = await confirmPropertyCsvImport(selectedFile);
      setImportResult(result);
    } catch (error) {
      setImportResult(null);
      setErrorMessage(error.message || "Unable to import CSV file.");
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setPreview(null);
    setImportResult(null);
    setErrorMessage("");
  };

  const summary = preview?.summary;

  return (
    <div className="admin-dashboard admin-csv-import-dash">
      <header className="admin-dashboard__header">
        <div>
          <h1 className="admin-dashboard__title">CSV Import</h1>
          <p className="admin-dashboard__subtitle">
            Import property records from CSV — staged, validated, then published
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          <a
            href={getPropertyImportTemplateUrl()}
            download="midpoint-property-import-template.csv"
            className="admin-dashboard__action-btn"
          >
            Download Template
          </a>
          {(preview || importResult) && (
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={handleReset}
            >
              Start Over
            </button>
          )}
          {onClose && (
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={onClose}
            >
              ← Dashboard
            </button>
          )}
        </div>
      </header>

      {importResult ? (
        <>
          <section className="admin-dashboard__kpis">
            <div className="admin-dash__kpi-card">
              <span className="admin-dash__kpi-label">Received</span>
              <span className="admin-dash__kpi-value">
                {importResult.summary.recordsReceived}
              </span>
            </div>
            <div className="admin-dash__kpi-card">
              <span className="admin-dash__kpi-label">Inserted</span>
              <span className="admin-dash__kpi-value admin-dash__kpi-value--green">
                {importResult.summary.recordsInserted}
              </span>
            </div>
            <div className="admin-dash__kpi-card">
              <span className="admin-dash__kpi-label">Updated</span>
              <span className="admin-dash__kpi-value">
                {importResult.summary.recordsUpdated}
              </span>
            </div>
            <div className="admin-dash__kpi-card">
              <span className="admin-dash__kpi-label">Failed</span>
              <span
                className={`admin-dash__kpi-value${
                  importResult.summary.recordsFailed > 0 ? " admin-dash__kpi-value--red" : ""
                }`}
              >
                {importResult.summary.recordsFailed}
              </span>
            </div>
            {importResult.summary.recordsSkipped > 0 && (
              <div className="admin-dash__kpi-card">
                <span className="admin-dash__kpi-label">Skipped</span>
                <span className="admin-dash__kpi-value admin-dash__kpi-value--amber">
                  {importResult.summary.recordsSkipped}
                </span>
              </div>
            )}
          </section>

          <section className="admin-dash__card admin-dash__card--wide admin-csv-import-dash__result">
            <div className="admin-csv-import-dash__result-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <div>
                <h2 className="admin-dash__card-title">{importResult.message}</h2>
                <span className="muted">Run ID: {importResult.ingestionRunId}</span>
              </div>
            </div>
            <p className="admin-csv-import-dash__footnote">
              Verify imported records via the Real Estate map layer or the Broker Feeds page.
            </p>
          </section>
        </>
      ) : (
        <>
          <section className="admin-dash__card admin-dash__card--wide admin-csv-import-dash__upload">
            <div className="admin-csv-import-dash__upload-zone">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <div className="admin-csv-import-dash__upload-content">
                <label className="admin-csv-import-dash__file-label">
                  <span className="admin-dashboard__action-btn admin-dashboard__action-btn--primary">
                    Choose CSV File
                  </span>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleFileChange}
                    className="admin-csv-import-dash__file-input"
                  />
                </label>
                {selectedFile ? (
                  <p className="admin-csv-import-dash__file-meta">
                    {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
                  </p>
                ) : (
                  <p className="muted">Select a .csv file to begin</p>
                )}
              </div>
            </div>
            <div className="admin-csv-import-dash__actions">
              <button
                type="button"
                className="admin-dashboard__action-btn"
                onClick={handlePreview}
                disabled={!selectedFile || previewLoading}
              >
                {previewLoading ? "Previewing…" : "Preview CSV"}
              </button>
              <button
                type="button"
                className="admin-dashboard__action-btn admin-dashboard__action-btn--primary"
                onClick={handleConfirm}
                disabled={!selectedFile || !preview?.summary?.validRows || confirmLoading}
              >
                {confirmLoading ? "Importing…" : "Confirm Import"}
              </button>
            </div>
            {errorMessage ? (
              <p className="admin-csv-import-dash__error">{errorMessage}</p>
            ) : null}
          </section>

          {preview && (
            <>
              <section className="admin-dashboard__kpis">
                <div className="admin-dash__kpi-card">
                  <span className="admin-dash__kpi-label">Total Rows</span>
                  <span className="admin-dash__kpi-value">{summary.totalRows}</span>
                </div>
                <div className="admin-dash__kpi-card">
                  <span className="admin-dash__kpi-label">Valid</span>
                  <span className="admin-dash__kpi-value admin-dash__kpi-value--green">
                    {summary.validRows}
                  </span>
                </div>
                <div className="admin-dash__kpi-card">
                  <span className="admin-dash__kpi-label">Invalid</span>
                  <span
                    className={`admin-dash__kpi-value${
                      summary.invalidRows > 0 ? " admin-dash__kpi-value--red" : ""
                    }`}
                  >
                    {summary.invalidRows}
                  </span>
                </div>
                <div className="admin-dash__kpi-card">
                  <span className="admin-dash__kpi-label">Warnings</span>
                  <span
                    className={`admin-dash__kpi-value${
                      summary.warningRows > 0 ? " admin-dash__kpi-value--amber" : ""
                    }`}
                  >
                    {summary.warningRows}
                  </span>
                </div>
                <div className="admin-dash__kpi-card">
                  <span className="admin-dash__kpi-label">Updates</span>
                  <span className="admin-dash__kpi-value">{summary.updateCandidates}</span>
                </div>
              </section>

              {preview.errors?.length ? (
                <section className="admin-dash__card admin-dash__card--wide admin-csv-import-dash__table-card">
                  <div className="admin-dash__card-header">
                    <h2 className="admin-dash__card-title">Validation Errors</h2>
                    <span className="admin-dash__card-subtitle">
                      {preview.errors.length} issue{preview.errors.length !== 1 ? "s" : ""} found
                    </span>
                  </div>
                  <div className="admin-csv-import-dash__table-wrap">
                    <table className="admin-data-health-table">
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Field</th>
                          <th>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.errors.map((error, index) => (
                          <tr key={`${error.rowNumber}-${error.field}-${index}`}>
                            <td>{error.rowNumber}</td>
                            <td><code>{error.field}</code></td>
                            <td>{error.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : null}

              {preview.sampleRows?.length ? (
                <section className="admin-dash__card admin-dash__card--wide admin-csv-import-dash__table-card">
                  <div className="admin-dash__card-header">
                    <h2 className="admin-dash__card-title">Sample Rows</h2>
                    <span className="admin-dash__card-subtitle">
                      {preview.sampleRows.length} row{preview.sampleRows.length !== 1 ? "s" : ""} shown
                    </span>
                  </div>
                  <div className="admin-csv-import-dash__table-wrap">
                    <table className="admin-data-health-table">
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Status</th>
                          <th>Title</th>
                          <th>Price</th>
                          <th>Type</th>
                          <th>Area</th>
                          <th>Warnings</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.sampleRows.map((row) => (
                          <tr key={row.rowNumber}>
                            <td>{row.rowNumber}</td>
                            <td>{row.status}</td>
                            <td>{row.property?.title}</td>
                            <td>
                              {formatPropertyPrice(
                                row.property?.price,
                                row.property?.currencyCode || "COP"
                              )}
                            </td>
                            <td>{formatPropertyLabel(row.property?.propertyType)}</td>
                            <td>{row.property?.areaCode || row.property?.city || "—"}</td>
                            <td>{row.warnings?.length ? row.warnings.join(" · ") : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AdminPropertyImportPage;
